/**
 * The client portal's own top-level app — a full split from the staff
 * `App()`, not a branch inside it.
 *
 * `/admin` (see `App.tsx`) is the closest existing precedent for "a second
 * screen inside the same bundle," but it still runs through the staff
 * `useQuery(["me"], fetchMe)` and `me.isError -> <SignIn>` gate before
 * branching — a client-portal identity has no `/api/me` row at all and must
 * never trigger that fetch. Splitting at the component boundary, before any
 * staff-only hook runs, is what keeps the two identities from ever touching.
 */
import { ZERO, add } from "@dantria/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useState } from "react";
import { PortalSignIn } from "./PortalSignIn";
import { ApiError, api, fmtAmount as fmt, unwrap } from "./api";
import { portalSignOut } from "./portal-auth-client";
import {
  Button,
  ChevronButton,
  Empty,
  ErrorNote,
  HoverCard,
  Loading,
  Panel,
  Stat,
  Table,
  Td,
  Th,
  input,
} from "./ui";

async function fetchPortalMe() {
  return unwrap(await api.api.portal.me.$get());
}

async function fetchPortalOverview() {
  return unwrap(await api.api.portal.overview.$get());
}

async function fetchProjectStatusEvents(projectId: string) {
  return unwrap(
    await api.api.portal.projects[":id"]["status-events"].$get({ param: { id: projectId } }),
  );
}

type PortalStatusEvent = Awaited<ReturnType<typeof fetchProjectStatusEvents>>["events"][number];

// Same palette and pill treatment as the staff-side Status panel
// (ProjectDetail.tsx) — a client reading "on hold" should recognize it as the
// same signal staff sees, not a differently-styled word.
const PROJECT_STATUS_DOT: Record<string, string> = {
  draft: "bg-neutral-400",
  active: "bg-green-600",
  on_hold: "bg-amber-600",
  completed: "bg-blue-600",
  cancelled: "bg-neutral-300",
};

const PROJECT_STATUS_BADGE: Record<string, string> = {
  draft: "bg-neutral-100 text-neutral-700 ring-neutral-300",
  active: "bg-green-50 text-green-700 ring-green-300",
  on_hold: "bg-amber-50 text-amber-700 ring-amber-300",
  completed: "bg-blue-50 text-blue-700 ring-blue-300",
  cancelled: "bg-neutral-100 text-neutral-400 ring-neutral-300 line-through",
};

/** A colored dot-and-pill status chip — `current` gets the filled color, past entries stay a neutral outline. */
function StatusBadge({ status, current = true }: { status: string; current?: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold ring-1 ${
        current
          ? `${PROJECT_STATUS_BADGE[status] ?? "bg-neutral-100 text-neutral-700 ring-neutral-300"} shadow-sm`
          : "bg-white text-neutral-500 ring-neutral-200"
      }`}
    >
      <span
        className={`h-2 w-2 shrink-0 rounded-full ${PROJECT_STATUS_DOT[status] ?? "bg-neutral-300"}`}
      />
      {status.replace(/_/g, " ")}
    </span>
  );
}

function ArrowRight() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className="shrink-0 text-neutral-300"
      role="presentation"
      aria-hidden
    >
      <path
        d="M6 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function PortalApp() {
  const [signedOut, setSignedOut] = useState(false);
  const qc = useQueryClient();

  // A 401 here just means "not signed in yet" — not a failure worth a toast.
  const me = useQuery({
    queryKey: ["portal-me"],
    queryFn: fetchPortalMe,
    retry: false,
    meta: { silent: true },
  });

  // The invite-acceptance path replaces the URL with /portal once a session
  // exists, but a stale /portal/invite/:token in the address bar (back
  // button, a re-opened tab) shouldn't keep showing the invite screen once
  // signed in.
  useEffect(() => {
    if (me.data && window.location.pathname !== "/portal") {
      window.history.replaceState({}, "", "/portal");
    }
  }, [me.data]);

  if (me.isPending) return <Centered>Loading…</Centered>;

  if (me.isError || signedOut) {
    return (
      <PortalSignIn
        onSignedIn={() => {
          setSignedOut(false);
          qc.invalidateQueries();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      <header className="sticky top-0 z-30 h-14 border-b border-neutral-200 bg-white/85 backdrop-blur-md">
        <div className="mx-auto flex h-full max-w-[1200px] items-center gap-4 px-6">
          <span className="whitespace-nowrap font-[family-name:--font-display] text-lg font-bold tracking-tight text-neutral-900">
            {me.data.company.name}
          </span>
          <span className="text-sm text-neutral-400">{me.data.client.name}</span>
          <span className="ml-auto truncate text-sm text-neutral-500">
            {me.data.portalUser.email}
          </span>
          <button
            type="button"
            onClick={async () => {
              await portalSignOut();
              qc.clear();
              setSignedOut(true);
            }}
            className="text-sm text-neutral-600 underline underline-offset-4 hover:text-neutral-900"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-6 py-8">
        <Overview clientName={me.data.client.name} />
      </main>
    </div>
  );
}

function Overview({ clientName }: { clientName: string }) {
  const overview = useQuery({ queryKey: ["portal-overview"], queryFn: fetchPortalOverview });
  // Which projects have their comment thread open — a set, so more than one
  // can be read side by side.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (overview.isPending) return <Loading />;
  if (overview.isError) {
    return <ErrorNote>{(overview.error as Error).message}</ErrorNote>;
  }

  const projects = overview.data.projects;
  const currency = projects[0]?.currency ?? "USD";
  const totals = projects.reduce(
    (acc, p) => ({
      contract: add(acc.contract, p.contractValue),
      billed: add(acc.billed, p.billed),
      paid: add(acc.paid, p.paid),
      outstanding: add(acc.outstanding, p.outstanding),
    }),
    { contract: ZERO, billed: ZERO, paid: ZERO, outstanding: ZERO },
  );

  return (
    <Panel title={`${clientName}'s projects`}>
      {projects.length === 0 ? (
        <Empty title="No projects yet">Nothing has been set up for you to see yet.</Empty>
      ) : (
        <>
          <div className="mb-6 grid gap-3 sm:grid-cols-4">
            <Stat label="Contract" value={fmt(totals.contract)} currency={currency} />
            <Stat label="Billed" value={fmt(totals.billed)} currency={currency} />
            <Stat label="Paid" value={fmt(totals.paid)} currency={currency} />
            <Stat
              label="Outstanding"
              value={fmt(totals.outstanding)}
              currency={currency}
              hint="Billed, not yet paid"
            />
          </div>

          <Table
            head={
              <>
                <Th>Code</Th>
                <Th>Project</Th>
                <Th>Status</Th>
                <Th right>Contract</Th>
                <Th right>Billed</Th>
                <Th right>Paid</Th>
                <Th right>Outstanding</Th>
                <Th />
              </>
            }
          >
            {projects.map((p) => {
              const isOpen = expanded.has(p.id);
              return (
                <Fragment key={p.id}>
                  <tr className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100">
                    <Td mono muted>
                      {p.code}
                    </Td>
                    <Td>{p.name}</Td>
                    <Td>
                      <StatusBadge status={p.status} />
                    </Td>
                    <Td right mono>
                      {fmt(p.contractValue, p.currency)}
                    </Td>
                    <Td right mono muted>
                      {fmt(p.billed, p.currency)}
                    </Td>
                    <Td right mono>
                      {fmt(p.paid, p.currency)}
                    </Td>
                    <Td right mono>
                      {fmt(p.outstanding, p.currency)}
                    </Td>
                    <Td right>
                      <ChevronButton open={isOpen} onClick={() => toggle(p.id)} label="status" />
                    </Td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={8} className="bg-neutral-50 px-4 py-4">
                        <ProjectStatusTimeline projectId={p.id} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </Table>
        </>
      )}
    </Panel>
  );
}

/**
 * Every status this project has moved through, and why — not just the word
 * "on hold" in the table row above, but the reason staff gave for it. Same
 * presentation as staff's own project page: a chain of badges, oldest to
 * newest, each one hoverable for its reason and replies, clickable to open
 * the reply thread inline below the whole chain.
 */
function ProjectStatusTimeline({ projectId }: { projectId: string }) {
  const q = useQuery({
    queryKey: ["portal-status-events", projectId],
    queryFn: () => fetchProjectStatusEvents(projectId),
  });
  const [openEventId, setOpenEventId] = useState<string | null>(null);

  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorNote>{(q.error as Error).message}</ErrorNote>;

  if (q.data.events.length === 0) {
    return <p className="text-sm text-neutral-500">No status updates yet.</p>;
  }

  const events = [...q.data.events].reverse();
  const openEvent = q.data.events.find((ev) => ev.id === openEventId);

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-y-3">
        {events.map((ev, i) => (
          <Fragment key={ev.id}>
            {i > 0 && <ArrowRight />}
            <StatusEventBadge
              event={ev}
              isCurrent={i === events.length - 1}
              isOpen={ev.id === openEventId}
              onToggle={() => setOpenEventId((cur) => (cur === ev.id ? null : ev.id))}
            />
          </Fragment>
        ))}
      </div>

      {openEvent && <StatusDiscussion projectId={projectId} event={openEvent} />}
    </div>
  );
}

/**
 * One entry in the chain. Hovering it shows the reason and every reply in a
 * styled card (`HoverCard`, portaled to `document.body` so the table's own
 * scroll container can't clip it) and the badge scales up slightly to read
 * as clickable. Clicking it toggles the reply thread below the chain — see
 * `StatusDiscussion`.
 */
function StatusEventBadge({
  event,
  isCurrent,
  isOpen,
  onToggle,
}: {
  event: PortalStatusEvent;
  isCurrent: boolean;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <HoverCard
      trigger={
        <button
          type="button"
          onClick={onToggle}
          aria-pressed={isOpen}
          className={`rounded-full transition-transform duration-150 hover:scale-105 ${
            isOpen ? "outline outline-2 outline-offset-2 outline-accent-500" : ""
          }`}
        >
          <StatusBadge status={event.toStatus} current={isCurrent} />
        </button>
      }
    >
      <p className="font-medium text-neutral-700">{new Date(event.createdAt).toLocaleString()}</p>
      <p className="mt-1 text-neutral-600">
        {event.comment || <span className="italic text-neutral-400">No reason given</span>}
      </p>
      {event.comments.length > 0 && (
        <div className="mt-2 space-y-2 border-t border-neutral-100 pt-2">
          {event.comments.map((cm) => (
            <div key={cm.id}>
              <div className="mb-0.5 flex items-center gap-1.5 text-[11px] text-neutral-500">
                <span className="font-medium text-neutral-700">{cm.authorName}</span>
                {cm.authorType === "staff" && (
                  <span className="rounded-full bg-accent-50 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-accent-700">
                    team
                  </span>
                )}
              </div>
              <p className="whitespace-pre-wrap text-neutral-800">{cm.body}</p>
            </div>
          ))}
        </div>
      )}
      <p className="mt-2 text-[11px] italic text-neutral-400">Click to reply</p>
    </HoverCard>
  );
}

/**
 * The one write this portal can make: a reply on a specific status update,
 * never a status change itself — that stays read-only, enforced
 * server-side in `apps/api/src/routes/portal.ts`, not just left off this
 * form.
 */
function StatusDiscussion({
  projectId,
  event,
}: {
  projectId: string;
  event: PortalStatusEvent;
}) {
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const text = body.trim();
    if (!text) return;
    setError(null);
    setBusy(true);
    try {
      await unwrap(
        await api.api.portal["status-events"][":id"].comments.$post({
          param: { id: event.id },
          json: { body: text },
        }),
      );
      setBody("");
      qc.invalidateQueries({ queryKey: ["portal-status-events", projectId] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 border-t border-neutral-100 pt-4 text-sm">
      <p className="mb-1 font-medium text-neutral-800">
        {new Date(event.createdAt).toLocaleString()}
      </p>
      <p className="mb-3 text-neutral-600">
        {event.comment || <span className="italic text-neutral-400">No reason given</span>}
      </p>

      {event.comments.length > 0 && (
        <div className="mb-3 space-y-2">
          {event.comments.map((cm) => (
            <div key={cm.id} className="rounded-lg border border-neutral-200 bg-neutral-50 p-3">
              <div className="mb-0.5 flex items-center gap-2 text-xs text-neutral-500">
                <span className="font-medium text-neutral-700">{cm.authorName}</span>
                {cm.authorType === "staff" && (
                  <span className="rounded-full bg-accent-50 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-accent-700">
                    team
                  </span>
                )}
                <span>{new Date(cm.createdAt).toLocaleString()}</span>
              </div>
              <p className="whitespace-pre-wrap text-neutral-800">{cm.body}</p>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={submit} className="flex items-end gap-2">
        <textarea
          className={input}
          rows={2}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Ask a question or leave a note…"
        />
        <Button type="submit" disabled={busy || !body.trim()}>
          {busy ? "Posting…" : "Post"}
        </Button>
      </form>
      {error && <ErrorNote>{error}</ErrorNote>}
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50">
      <div className="text-center">{children}</div>
    </div>
  );
}
